import React, { useEffect } from 'react';
import { X, Trophy, Award, TrendingUp, Target } from 'lucide-react';

const AchievementNotification = ({ achievement, onClose }) => {
  useEffect(() => {
    // Auto-dismiss after 5 seconds
    const timer = setTimeout(() => {
      onClose();
    }, 5000);

    return () => clearTimeout(timer);
  }, [onClose]);

  const getAchievementIcon = (type) => {
    switch (type) {
      case 'global_top10':
      case 'regional_winner':
        return <Trophy className="h-8 w-8 text-yellow-500" />;
      case 'streak_master':
        return <Target className="h-8 w-8 text-orange-500" />;
      case 'perfect_week':
        return <Award className="h-8 w-8 text-blue-500" />;
      default:
        return <TrendingUp className="h-8 w-8 text-green-500" />;
    }
  };

  return (
    <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
      <div className="bg-white rounded-xl shadow-2xl p-4 max-w-sm border-2 border-yellow-400">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            {getAchievementIcon(achievement.achievement_type)}
          </div>
          
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-2xl">{achievement.badge_icon}</span>
              <h4 className="font-bold text-gray-900">Achievement Unlocked!</h4>
            </div>
            <p className="text-sm font-semibold text-gray-800">
              {achievement.achievement_name}
            </p>
            {achievement.metric_value && (
              <p className="text-xs text-gray-600 mt-1">
                Achieved: {achievement.metric_value}
              </p>
            )}
          </div>

          <button
            onClick={onClose}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Celebration animation */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-xl">
          <div className="confetti-animation"></div>
        </div>
      </div>
    </div>
  );
};

// Achievement Notification Manager Component
const AchievementNotificationManager = () => {
  const [achievements, setAchievements] = React.useState([]);

  // Function to show achievement (can be called from anywhere)
  const showAchievement = React.useCallback((achievement) => {
    setAchievements(prev => [...prev, { ...achievement, id: Date.now() }]);
  }, []);

  const dismissAchievement = React.useCallback((id) => {
    setAchievements(prev => prev.filter(a => a.id !== id));
  }, []);

  // Expose showAchievement globally (optional)
  React.useEffect(() => {
    window.showAchievement = showAchievement;
    return () => {
      delete window.showAchievement;
    };
  }, [showAchievement]);

  return (
    <>
      {achievements.map((achievement) => (
        <AchievementNotification
          key={achievement.id}
          achievement={achievement}
          onClose={() => dismissAchievement(achievement.id)}
        />
      ))}
    </>
  );
};

export { AchievementNotification, AchievementNotificationManager };
export default AchievementNotification;
